import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConfigProvider, Dropdown } from "antd";
import { CheckOutlined, DownOutlined } from "@ant-design/icons";

import LogoWhite from "../../../assets/image/logo-white.svg";
import { AUTH } from "../../config/lang";
import { DEFAULT_LANG, isKnownLang, LANGS, resolveLanguage, storeChosenLanguage } from "../../config/languages";
import { antdLocaleFor } from "../../theme/antd";
import { translate } from "../../util/util";

import "./auth.scss";

/* The rest of the app reads the locale off `me.language`, but on the auth screens there is no
   `me` yet — so these pages resolve it themselves: an explicit choice if one was ever made,
   otherwise a guess from the device time zone. See config/languages.js. */
export function useAuthLang() {
  const [lang, setLangState] = useState(resolveLanguage);

  const setLang = useCallback(code => {
    const next = isKnownLang(code) ? code : DEFAULT_LANG;
    /* Only a hand-picked language is ever written, which is exactly what makes the stored key
       mean "deliberate" and lets it outrank detection on every later visit. */
    storeChosenLanguage(next);
    setLangState(next);
  }, []);

  /* Screen readers and `lang`-sensitive font stacks both need this to match the copy. */
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(key => translate(AUTH, key, lang), [lang]);

  return { lang, setLang, t };
}

/**
 * Drops any visible validation error when the language changes, so no message is left sitting
 * in the language the user just switched away from. The field keeps its value and re-validates
 * on the next change or submit, in the new language.
 *
 * Re-translating in place would be nicer, but antd resolves a rule's `message` once and stores
 * the resolved string in form state; re-validating from here — even deferred a tick — replays
 * the *previous* language's message, so the error visibly lagged one language behind. Clearing
 * is deterministic and never shows the wrong language.
 */
export function useClearErrorsOnLangChange(form, lang) {
  const first = useRef(true);
  useEffect(() => {
    /* Nothing can be showing on mount, and clearing here would fight autoFocus. */
    if (first.current) {
      first.current = false;
      return;
    }
    const failing = form
      .getFieldsError()
      .filter(f => f.errors.length)
      .map(f => ({ name: f.name, errors: [] }));
    if (failing.length) form.setFields(failing);
  }, [lang, form]);
}

function LanguagePicker({ lang, setLang, t }) {
  const [open, setOpen] = useState(false);
  const current = LANGS.find(l => l.code === lang) || LANGS[0];

  const items = LANGS.map(l => ({
    key: l.code,
    label: (
      <span className="auth-langitem">
        <span>{l.label}</span>
        {l.native ? <span className="auth-langitem__native">{l.native}</span> : null}
        {l.code === lang ? <CheckOutlined className="auth-langitem__tick" /> : null}
      </span>
    ),
  }));

  return (
    <div className="auth-langwrap">
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={["click"]}
        placement="bottomRight"
        classNames={{ root: "auth-langmenu" }}
        menu={{ items, selectable: true, selectedKeys: [lang], onClick: ({ key }) => setLang(key) }}>
        <button className="auth-lang" type="button" aria-label={t("language")} aria-expanded={open}>
          <span>{current.label}</span>
          <DownOutlined className="auth-lang__chev" />
        </button>
      </Dropdown>
    </div>
  );
}

/**
 * Two-panel auth frame: brand story on the left, form on the right; stacks under 900px.
 * `position: fixed` on the root makes it full-bleed regardless of #app.
 */
export default function AuthShell({ lang, setLang, t, children }) {
  const headline = t("headline") || "";

  return (
    <ConfigProvider locale={antdLocaleFor(lang)}>
      <div className="auth">
        <aside className="auth-brand">
          <img className="auth-logo" src={LogoWhite} alt="Picsell" />

          <div>
            {/* The dictionary string carries its own line break (white-space: pre-line) */}
            <h1 className="auth-headline">{headline}</h1>
            <p className="auth-subhead">{t("subhead")}</p>
          </div>

          <ul className="auth-features">
            {["feat1", "feat2", "feat3"].map(key => (
              <li key={key}>
                <CheckOutlined className="auth-features__tick" aria-hidden="true" />
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
        </aside>

        <LanguagePicker lang={lang} setLang={setLang} t={t} />

        <main className="auth-pane">{children}</main>
      </div>
    </ConfigProvider>
  );
}
